<template>
  <component
    :is="href ? 'a' : 'button'"
    :href="href"
    :type="href ? undefined : type"
    :class="classes"
    :style="cssVars"
    :aria-label="ariaLabel || undefined"
    :disabled="disabled"
    @click="handleClick"
  >
    <span v-if="hasIcon && iconPosition === 'left'" class="btn__icon" :class="{ 'animate-spin': iconSpin }">
      <Icon :name="icon" :size="iconSize" />
    </span>

    <span v-if="hasDefaultSlotContent" class="btn__content">
      <slot></slot>
    </span>

    <span v-if="hasIcon && iconPosition === 'right'" class="btn__icon">
      <Icon :name="icon" :size="iconSize" />
    </span>
  </component>
</template>

<script setup lang="ts">
import { computed, Fragment, useSlots, VNode } from "vue";
import Icon from "../Icon.vue";

import "./Button.scss";

const slots = useSlots();

function isRenderable(node: VNode | any): boolean {
  if (!node) return false;

  // Комментарии от v-if="false"
  if (node.type === Comment) return false;

  // Текстовые ноды: игнорируем пробелы и переносы
  if (node.type === Text) {
    return String(node.children ?? "").trim().length > 0;
  }

  // Фрагменты: проверяем детей рекурсивно
  if (node.type === Fragment) {
    const children = Array.isArray(node.children) ? node.children : [];
    return children.some(isRenderable);
  }

  if (node.children === "v-if") {
    return false;
  }

  // Всё остальное считаем реальным контентом
  return true;
}

const hasDefaultSlotContent = computed(() => {
  const nodes = slots.default?.() ?? [];
  return nodes.some(isRenderable);
});

const props = defineProps({
  color: { type: String as () => "green" | "blue" | "transparent", default: "green" },
  appearance: { type: String as () => "none" | "outline" | "solid", default: "solid" },
  // hover can be: 'green' | 'blue' | 'opacity' | false | any CSS color string
  hover: { type: [String, Boolean], default: "opacity" },
  icon: { type: String, default: "" },
  iconPosition: { type: String as () => "left" | "right", default: "left" },
  iconSize: { type: [Number, String], default: 16 },
  iconSpin: { type: Boolean, default: false },
  href: { type: String, default: "" },
  type: { type: String as () => "button" | "submit" | "reset", default: "button" },
  disabled: { type: Boolean, default: false },
  ariaLabel: { type: String, default: "" },
  justify: { type: String as () => "left" | "center" | "right", default: "center" },
  style: { type: Object as () => Record<string, string>, default: () => ({}) },
});

const emit = defineEmits<{
  (e: "click", ev: MouseEvent): void;
}>();

const hasIcon = computed(() => !!props.icon);

const hoverClass = computed(() => {
  if (props.hover === true) return "btn--hover-opacity";
  if (props.hover === "opacity") return "btn--hover-opacity";
  if (props.hover === "green") return "btn--hover-green";
  if (props.hover === "blue") return "btn--hover-blue";
  return "";
});

const justifyClass = computed(() => {
  if (props.justify === "left") return "btn--justify-left";
  if (props.justify === "center") return "btn--justify-center";
  if (props.justify === "right") return "btn--justify-right";
  return "";
});

const classes = computed(() => {
  return ["btn", `btn--color-${props.color}`, `btn--appearance-${props.appearance}`, props.disabled ? "is-disabled" : "", hoverClass.value, justifyClass.value];
});

const cssVars = computed(() => {
  const vars: Record<string, string> = {};
  // If hover is a custom CSS color string (not our named variants), set it as --btn-hover-color
  if (typeof props.hover === "string" && props.hover.trim() !== "") {
    const v = props.hover.trim();
    if (v !== "green" && v !== "blue" && v !== "opacity") {
      vars["--btn-hover-color"] = v;
      // when a custom color is provided, text becomes white on hover for solid/outline
      vars["--btn-hover-text"] = "white";
    }
    if (v === "opacity") {
      vars["--btn-hover-opacity"] = "0.92";
    }
  } else if (props.hover === true) {
    vars["--btn-hover-opacity"] = "0.92";
  }
  return { ...vars, ...props.style };
});

function handleClick(e: MouseEvent) {
  if (props.disabled) {
    e.preventDefault();
    e.stopImmediatePropagation();
    return;
  }
  emit("click", e);
}
</script>

<style scoped lang="scss">
// Basic tokens
$green: #00bf7b;
$green-dark: darken($green, 6%);
$blue: #2b6cff;
$blue-dark: darken($blue, 6%);
$transparent: transparent;
$btn-radius: 6px;
$btn-padding: 0.5rem 0.9rem;
$transition-fast: 150ms ease-in-out;

.btn {
  margin: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: $btn-padding;
  border-radius: $btn-radius;
  font-weight: 600;
  cursor: pointer;
  line-height: 1;
  user-select: none;
  transition:
    background $transition-fast,
    color $transition-fast,
    border-color $transition-fast,
    opacity $transition-fast;
  text-decoration: none; // for anchors

  &.is-disabled {
    opacity: 0.6;
    cursor: not-allowed;
    pointer-events: none;
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    svg {
      display: block;
    }
  }

  &__content {
    display: inline-flex;
    align-items: center;
  }
}

// Color / appearance variants
// Green
.btn--color-green.btn--appearance-solid {
  background: $green;
  color: white;
  border: none;
}
.btn--color-green.btn--appearance-outline {
  background: transparent;
  color: $green;
  border: 1px solid $green;
}
.btn--color-green.btn--appearance-none {
  background: transparent;
  color: $green;
  border: none;
}

// Blue
.btn--color-blue.btn--appearance-solid {
  background: $blue;
  color: white;
  border: none;
}
.btn--color-blue.btn--appearance-outline {
  background: transparent;
  color: $blue;
  border: 1px solid $blue;
}
.btn--color-blue.btn--appearance-none {
  background: transparent;
  color: $blue;
  border: none;
}

// Transparent (neutral)
.btn--color-transparent {
  color: inherit;
}
.btn--color-transparent.btn--appearance-solid {
  background: rgba(0, 0, 0, 0.06);
  color: inherit;
  border: none;
}
.btn--color-transparent.btn--appearance-outline {
  background: transparent;
  border: 1px solid rgba(0, 0, 0, 0.12);
}
.btn--color-transparent.btn--appearance-none {
  background: transparent;
  border: none;
}

// Hover states
// Named hover variants: green, blue, opacity
.btn--hover-green.btn--appearance-solid:hover,
.btn--hover-green.btn--appearance-outline:hover,
.btn--hover-green.btn--appearance-none:hover {
  background: $green;
  color: white;
  border-color: $green;
}

.btn--hover-blue.btn--appearance-solid:hover,
.btn--hover-blue.btn--appearance-outline:hover,
.btn--hover-blue.btn--appearance-none:hover {
  background: $blue;
  color: white;
  border-color: $blue;
}

// Opacity variant
.btn--hover-opacity:hover {
  opacity: var(--btn-hover-opacity, 0.92);
}

.btn--justify-left {
  justify-content: flex-start;
}

.btn--justify-center {
  justify-content: center;
}

.btn--justify-right {
  justify-content: flex-end;
}

// Custom color via --btn-hover-color (string hover)
.btn[style*="--btn-hover-color"].btn--appearance-solid:hover,
.btn[style*="--btn-hover-color"].btn--appearance-outline:hover,
.btn[style*="--btn-hover-color"].btn--appearance-none:hover {
  background: var(--btn-hover-color);
  color: var(--btn-hover-text, white);
  border-color: var(--btn-hover-color);
}

// Focus ring for keyboard accessibility (WCAG 2.4.7)
.btn:focus {
  outline: none;
}

.btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px color-mix(in oklch, var(--ring) 35%, transparent);
}

// Icon-only compactness
.btn--icon-only {
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}
</style>
